import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';
import { IrbEditorComponent } from "@irb/irb-editors/irb-editors.com";
import { IPortfolioResultResponse, IPortfolioResultACCRebal } from '@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model';
import { ComPrCashValuesGridLayoutService } from '@irb/irb-editors/editor-portfolio-result/com-pr-cash-values/com-pr-cash-values.grid-layout.service';
import { JasperSettings, ISortModel } from "@irb/irb-dialogs/dialog-pr-export-pdf/dialog-pr-export-pdf.component";

@Component({ standalone: false,
  selector: 'irb-com-pr-cash-values',
  templateUrl: './com-pr-cash-values.component.html',
  styleUrls: ['./com-pr-cash-values.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ComPrCashValuesComponent extends IrbEditorComponent {
  _data: IPortfolioResultResponse;
  @Output() change = new EventEmitter<boolean>();
  @ViewChild('comPrCashValuesAccountOverlay') comPrCashValuesAccountOverlay: TemplateRef<any>;
  @ViewChild('comPrCashValuesUnavailableCashOverlay') comPrCashValuesUnavailableCashOverlay: TemplateRef<any>;
  @Input() pdfExportConfig: JasperSettings;
  @Output() pdfExportConfigChange = new EventEmitter<JasperSettings>();

  grid: any;
  gridOptions: any;
  frameworkComponents: any;
  gridHeight: string = "";
  row: any;
  componentName: any ='portfolioResultSummaryCashValues-v001';
  isUiTest: boolean = false;
  popupParent:any;
  isDistHoldCashEnabled: boolean;
  isCashEqvEnabled: boolean;

  constructor(private gls: ComPrCashValuesGridLayoutService) {
    super();
    this.popupParent = document.querySelector("body");
    const irbAppConfig = this.as.getItem("IRB_APP_CONFIG");
    this.isDistHoldCashEnabled = irbAppConfig?.featureFlags?.isDistHoldCashEnabled || false;
    this.isCashEqvEnabled = irbAppConfig?.featureFlags?.isCashEqvEnabled || false;
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(this),
      floatingFilter: false,
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No accounts.</span>",
      context: { componentParent: this },
      domLayout: "autoHeight",
      alignedGrids: [],
      suppressHorizontalScroll: true
    };
    this.frameworkComponents = this.gls.getFrameworkComponents();
  }

  onDataChange() {
    this.grid?.api.setGridOption('rowData', this.data.accRebalDTOList);
    if (this.grid) { this.updateGridFooter(); }
    this.grid?.api.refreshHeader();
  }

  onGridReady(params: any) {
    this.grid = params;
    
    if(this.data.accRebalDTOList.length > 20) {
      this.grid.api.setGridOption('domLayout', 'normal');
      this.gridHeight = "200px";
    } else if(this.data.accRebalDTOList.length === 1) {
      this.grid.api.setGridOption('domLayout', 'normal');
      this.gridHeight = "124px";
    } else {
      this.grid.api.setGridOption('domLayout', 'autoHeight');
      this.gridHeight = "100%";
    }

    this.updateGridFooter();
  }

  
  @Input()
  get data(): any {
    return this._data;
  }

  set data(d: any) {
    this._data = d;
  }

  isAllInRange(paramKey: string) {
    let inRangeCount = 0;

    this.data.accRebalDTOList.forEach((r: any) => {
      if(r[paramKey]) {
        inRangeCount++;
      }
    });

    return inRangeCount === this.data.accRebalDTOList.length;
  }

  getInRangeCount(paramKey: string) {
    let inRangeCount = 0;

    this.data.accRebalDTOList.forEach((r: any) => {
      if(r[paramKey]) {
        inRangeCount++;
      }
    });

    return inRangeCount + " of " + this.data.accRebalDTOList.length;
  }

  updateGridFooter() {
    const footer = {
      accountNum: "Portfolio Total",
      totalCash: 0,
      availCash: 0,
      unavailCash: 0,
      cashAdj: 0,
      afterRebalTotalCash: 0,
      afterRebalAvailAmt: 0,
      cashEquivAvail: 0,
      cashEquivAfterAdj: 0,
      cashAndEquivalentsBefore: 0,
      cashAndEquivalentsAfter: 0,
      mktValue: ''
    };

    this.data.accRebalDTOList.forEach((r: IPortfolioResultACCRebal) => {
      footer.totalCash += r.totalCash;
      footer.availCash += r.availCash;
      footer.unavailCash += r.unavailCash;
      footer.cashAdj += r.cashAdj;
      footer.afterRebalTotalCash += r.afterRebalTotalCash;
      footer.afterRebalAvailAmt += r.afterRebalAvailAmt;
      footer.cashEquivAvail += r.cashEquivAvail;
      footer.cashEquivAfterAdj += r.cashEquivAfterAdj;
      footer.cashAndEquivalentsBefore += (r.availCash + r.cashEquivAvail);
      footer.cashAndEquivalentsAfter += (r.afterRebalAvailAmt + r.cashEquivAfterAdj);
    });
    this.grid?.api.setGridOption('pinnedBottomRowData',[footer]);
  }

  getTemplate(params: any) {
    this.row = params.data;

    if(params.column.colId.includes("accountNum")) {
        return this.comPrCashValuesAccountOverlay;
    } else if(params.column.colId.includes("unavailCash")) {
        return this.comPrCashValuesUnavailableCashOverlay;
    } else {
        throw new Error('Could not find template for colId: ' + params.column.colId);
    }
  }
  
  resetGridColumns() {
    localStorage.removeItem(this.componentName);
    this.grid.api.resetColumnState();
    //this.grid.api.sizeColumnsToFit();
  }

  onColumnVisible(_ev: any) {
      let sortModel = this.grid?.api.getColumnState().filter((col:any) => col.sort !== null);

      if(sortModel?.length > 0) {
          this.pdfExportConfig.cashSortModel = [];

          sortModel.forEach((m: any) => {
              this.pdfExportConfig.cashSortModel.push({
                  sort: m.colId,
                  order: m.sort
              } as ISortModel);
          });

          this.pdfExportConfigChange.emit(this.pdfExportConfig);
      }
  }
}
